import { zodResolver } from "@hookform/resolvers/zod";
import { CreateProductGroupInput } from "generated/graphql";
import { useForm, Controller } from "react-hook-form";
import { Card, Grid } from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import FormField from "components/FormField/FormField";
import MDButton from "components/MDButton";
import AutoComplete from "modules/AutoComplete/AutoComplete";
import ProductOptions from "modules/products/ProductOptions";
import { z } from "zod";

const schema = z.object({
  name: z.string().min(1, "Name is required"),
  productIds: z.array(z.string()).min(1, "At least one product is required"),
});

const defaultValues = {
  name: "",
  productIds: [],
};

interface ProductGroupCreateFormProps {
  onSubmit: (values: CreateProductGroupInput) => Promise<{ success: boolean }>;
  loading: boolean;
}

export default function ProductGroupCreateForm({ onSubmit, loading }: ProductGroupCreateFormProps) {
  const {
    handleSubmit,
    register,
    control,
    formState: { errors },
    reset,
  } = useForm<CreateProductGroupInput & { productIds: string[] }>({
    resolver: zodResolver(schema),
    defaultValues,
  });

  return (
    <Card>
      <MDBox
        p={3}
        component="form"
        role="form"
        onSubmit={handleSubmit(async (values) => {
          const { productIds, ...restValues } = values;
          const input: CreateProductGroupInput = {
            ...restValues,
            products: {
              sync: productIds,
            },
          };
          const result = await onSubmit(input);
          if (result.success) {
            reset();
          }
        })}
      >
        <MDBox mt={1}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <FormField
                type="text"
                label="Name"
                placeholder="Enter product group name"
                error={errors.name}
                {...register("name")}
              />
            </Grid>
            <Grid item xs={12}>
              <ProductOptions>
                {({ productOptions, loading: productsLoading, search }) => (
                  <Controller
                    name="productIds"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <AutoComplete
                        options={productOptions}
                        field={field}
                        loading={productsLoading}
                        error={error?.message ? error : ""}
                        label="Select Products"
                        search={search}
                        multiple={true}
                        disableCloseOnSelect={true}
                      />
                    )}
                  />
                )}
              </ProductOptions>
            </Grid>
            <Grid item xs={12} display="flex" justifyContent="space-between">
              <MDButton type="submit" variant="gradient" color="success" disabled={loading}>
                Create Product Group
              </MDButton>
            </Grid>
          </Grid>
        </MDBox>
      </MDBox>
    </Card>
  );
}
